/**
 * Echo Hunt -- the lore drafting helpers (P10.5, §8.5 + §8.7).
 *
 * PURE module: no React, no `store`, no DB imports. It takes an `LLMBackend` as a parameter (the same
 * seam every other step uses, so tests run against `MockLLMBackend`), and falls back to the rng pools in
 * `Worlds.ts` when there is no backend at all -- so the character/world editors work offline instead of
 * presenting a button that does nothing.
 *
 * ## Two rules this module enforces, not the prompt
 *
 * 1. **Only the requested field is copied.** The reply always carries the whole object (see the
 *    `Prompts/Lore.ts` header), and a model asked to draft a name will often "helpfully" rewrite the
 *    disposition too. `ApplyCharacterDraft` copies `field`'s keys and nothing else, so a single-field
 *    request cannot silently change a value the user is happy with.
 * 2. **Out-of-range numbers are clamped, not rejected.** A reply of `-1.4` is a usable draft with a
 *    small error in it; rejecting it would burn a retry to fix arithmetic. The same posture as the rest
 *    of the pipeline (salvage what is coercible, report what is not).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.7, §16.2 P10.5.
 */
import {CallLLM, LLMBackend} from "./LLM.js";
import {EchoCharacter, EchoWorld} from "./Types.js";
import {
	CreateCharacter,
	CreateWorld,
	GenerateCharacterId,
	GenerateWorldId,
} from "./Worlds.js";
import {
	LoreCharacter_JSONSchema,
	LoreCharacter_SystemPrompt,
	LoreCharacter_UserPrompt,
	LoreCharacterField,
	LoreWorld_JSONSchema,
	LoreWorld_SystemPrompt,
	LoreWorld_UserPrompt,
	LoreWorldField,
} from "./Prompts/Lore.js";
import type {CharacterDraftKey, WorldDraftKey} from "./Prompts/Lore.js";

/** The result of a draft request. `draft` is always usable (either the model's, cleaned, or the rng
 * fallback); `error` records that the model path failed, so the UI can say "used the offline generator
 * because ..." instead of silently pretending the LLM answered. */
export type LoreDraftResult<T> = {
	draft: T;
	/** true when the model produced this draft (false = the rng fallback, or nothing was drafted) */
	generatedByModel: boolean;
	/** the fields the draft actually wrote. EMPTY means "there was nothing to do" -- the entry was already
	 * complete and Override was off -- which the UI reports instead of claiming a redraft happened. */
	keys: string[];
	error?: string|n;
};

/** Clamps a disposition into [-1, 1] and rounds to two decimals, so the number shown in the editor is
 * the number the weaver will be told about. */
export function ClampDisposition(value: number): number {
	if (!Number.isFinite(value)) return 0;
	return Math.round(Math.max(-1, Math.min(1, value)) * 100) / 100;
}

/** Clamps a craziness tolerance into [0, 1]. */
export function ClampTolerance(value: number): number {
	if (!Number.isFinite(value)) return .9;
	return Math.round(Math.max(0, Math.min(1, value)) * 100) / 100;
}

/** Which keys of `EchoCharacter` a `LoreCharacterField` request may replace. `all` is the whole entry
 * minus `id`/`generated` (both are the caller's business). */
export function CharacterFieldsFor(field: LoreCharacterField): (keyof EchoCharacter)[] {
	switch (field) {
		case "name": return ["name"];
		case "tagline": return ["tagline"];
		case "disposition": return ["values_cautionVsOpportunity"];
		case "notes": return ["personalityNotes"];
		case "all": return ["name", "tagline", "values_cautionVsOpportunity", "personalityNotes"];
	}
}

export function WorldFieldsFor(field: LoreWorldField): (keyof EchoWorld)[] {
	switch (field) {
		case "name": return ["name"];
		case "bible": return ["bible"];
		case "tolerance": return ["crazinessTolerance"];
		case "all": return ["name", "bible", "crazinessTolerance"];
	}
}

// which fields the draft actually writes
// ==========

/** The character fields that are still EMPTY.
 *
 * "Draft all fields" means "complete this entry", so with Override OFF it must only touch what is
 * blank -- otherwise pressing it after filling the form would silently overwrite the user's own text. */
export function EmptyCharacterFields(character: EchoCharacter): CharacterDraftKey[] {
	const empty: CharacterDraftKey[] = [];
	if (character.name.trim().length == 0) empty.push("name");
	if (character.tagline.trim().length == 0) empty.push("tagline");
	if (character.personalityNotes.trim().length == 0) empty.push("personalityNotes");
	// The disposition counts as empty at exactly 0, because 0 is the neutral value `NewCharacter` seeds.
	// Without this, "Draft all fields" on a brand-new entry would leave every drafted character at 0 --
	// and §8.2 wants the axis SPANNED, since a pool clustered at 0 makes everyone behave the same way.
	// A user who wants a deliberate 0 can set it after drafting, or use the -1/0/+1 buttons.
	if (character.values_cautionVsOpportunity == 0) empty.push("values_cautionVsOpportunity");
	return empty;
}

export function EmptyWorldFields(world: EchoWorld): WorldDraftKey[] {
	const empty: WorldDraftKey[] = [];
	if (world.name.trim().length == 0) empty.push("name");
	if (world.bible.trim().length == 0) empty.push("bible");
	return empty;
}

const ALL_CHARACTER_KEYS: CharacterDraftKey[] = ["name", "tagline", "values_cautionVsOpportunity", "personalityNotes"];
const ALL_WORLD_KEYS: WorldDraftKey[] = ["name", "bible", "crazinessTolerance"];

/** The exact keys a request will write, given the requested field and the Override flag.
 *
 *  - a per-field request always writes that one field;
 *  - `all` with Override writes everything ("give me a different character");
 *  - `all` without Override writes only the EMPTY fields ("complete this entry"), which is why pressing
 *    the button twice on a filled form does nothing by design -- the UI says so instead of pretending.
 *
 * Returns an EMPTY array when there is nothing to do, so the caller can skip the model call entirely
 * rather than spending tokens to be handed the same text back. */
export function CharacterDraftKeysFor(field: LoreCharacterField, base: EchoCharacter, override: boolean): CharacterDraftKey[] {
	if (field != "all") return [field as CharacterDraftKey];
	return override ? [...ALL_CHARACTER_KEYS] : EmptyCharacterFields(base);
}

export function WorldDraftKeysFor(field: LoreWorldField, base: EchoWorld, override: boolean): WorldDraftKey[] {
	if (field != "all") return [field as WorldDraftKey];
	return override ? [...ALL_WORLD_KEYS] : EmptyWorldFields(base);
}

/** Coerces one parsed lore reply onto `base`, writing ONLY `keys` (or `field`'s own keys when a verb is
 * given). Exported because it is the load-bearing half of the "only the requested field changes" rule,
 * and it is worth testing without an LLM in the loop.
 *
 * `keys` is what makes Override work: the caller decides WHICH fields are being redrawn (all of them when
 * overriding, only the empty ones when completing), and this function cannot write anything else. */
export function ApplyCharacterDraft(base: EchoCharacter, parsed: unknown, fieldOrKeys: LoreCharacterField|CharacterDraftKey[]): EchoCharacter {
	const raw = (parsed != null && typeof parsed == "object" && !Array.isArray(parsed)) ? parsed as Record<string, unknown> : {};
	const keys = Array.isArray(fieldOrKeys) ? fieldOrKeys : CharacterFieldsFor(fieldOrKeys) as CharacterDraftKey[];
	const next: EchoCharacter = {...base};

	for (const key of keys) {
		switch (key) {
			case "name": {
				const value = CoerceText(raw.name);
				if (value != null) next.name = value;
				break;
			}
			case "tagline": {
				const value = CoerceText(raw.tagline);
				if (value != null) next.tagline = value;
				break;
			}
			case "personalityNotes": {
				const value = CoerceText(raw.personalityNotes);
				if (value != null) next.personalityNotes = value;
				break;
			}
			case "values_cautionVsOpportunity": {
				// the model may answer with a string; accept it rather than rejecting a usable draft
				const value = typeof raw.values_cautionVsOpportunity == "number"
					? raw.values_cautionVsOpportunity
					: Number(raw.values_cautionVsOpportunity);
				if (Number.isFinite(value)) next.values_cautionVsOpportunity = ClampDisposition(value);
				break;
			}
			default: break;
		}
	}

	// a model-drafted entry is `generated` however it arrived; a hand-edited one keeps its own flag
	if (keys.length > 0) next.generated = true;
	return next;
}

export function ApplyWorldDraft(base: EchoWorld, parsed: unknown, fieldOrKeys: LoreWorldField|WorldDraftKey[]): EchoWorld {
	const raw = (parsed != null && typeof parsed == "object" && !Array.isArray(parsed)) ? parsed as Record<string, unknown> : {};
	const keys = Array.isArray(fieldOrKeys) ? fieldOrKeys : WorldFieldsFor(fieldOrKeys) as WorldDraftKey[];
	const next: EchoWorld = {...base};

	for (const key of keys) {
		switch (key) {
			case "name": {
				const value = CoerceText(raw.name);
				if (value != null) next.name = value;
				break;
			}
			case "bible": {
				const value = CoerceText(raw.bible);
				if (value != null) next.bible = value;
				break;
			}
			case "crazinessTolerance": {
				const value = typeof raw.crazinessTolerance == "number"
					? raw.crazinessTolerance
					: Number(raw.crazinessTolerance);
				if (Number.isFinite(value)) next.crazinessTolerance = ClampTolerance(value);
				break;
			}
			default: break;
		}
	}

	if (keys.length > 0) next.generated = true;
	return next;
}

/** A trimmed non-empty string, or null when the reply's value is missing/blank (in which case the
 * draft's existing text is kept -- never overwritten with an empty string). */
function CoerceText(value: unknown): string|null {
	if (typeof value != "string") return null;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
}

/** Starts a blank character for the editor's "Add character" button. A fresh id and a neutral
 * disposition; everything else empty, so the user (or an autogen) fills it in. */
export function NewCharacter(): EchoCharacter {
	return {
		id: GenerateCharacterId(),
		name: "",
		tagline: "",
		values_cautionVsOpportunity: 0,
		personalityNotes: "",
		generated: false,
	};
}

/** Starts a blank world for the Worlds tab's "Add world" button. Tolerance defaults high, per §8.5. */
export function NewWorld(): EchoWorld {
	return {
		id: GenerateWorldId(),
		name: "",
		bible: "",
		crazinessTolerance: .9,
		generated: false,
	};
}

export type GenerateCharacterDraftOptions = {
	field: LoreCharacterField;
	base: EchoCharacter;
	/** true = redraft every field instead of only the empty ones (`field: "all"` only) */
	override?: boolean;
	/** names already in the pool, so a draft does not duplicate a character the user already has. Without
	 * this a model offered an example list drifts back to the same few names, and two characters end up
	 * with the same first name. */
	avoidNames?: string[]|n;
	hint?: string|n;
	/** the `loreGenerator` role's model; null lets the backend decide */
	model?: string|n;
	maxAttempts?: number;
};

/** Drafts (part of) a character.
 *
 * Two things this owns rather than the caller:
 *  - **which keys get written** (`CharacterDraftKeysFor`). With Override OFF, "all fields" means "fill
 *    what is empty", so pressing the button on a filled form is a genuine no-op and is REPORTED as one
 *    (`keys: []`, no model call) instead of spending tokens to be handed the same text back.
 *  - **the avoid-names retry**. A model given a list of example names reproduces one of them sooner or
 *    later, so if the reply's name is already in the pool it is asked once more with that name named as
 *    forbidden, and only then accepted.
 */
export async function GenerateCharacterDraft(backend: LLMBackend|n, options: GenerateCharacterDraftOptions): Promise<LoreDraftResult<EchoCharacter>> {
	const keys = CharacterDraftKeysFor(options.field, options.base, options.override == true);
	if (keys.length == 0) {
		// nothing is missing and Override is off -- deliberately no model call (see above)
		return {draft: options.base, generatedByModel: false, keys};
	}

	if (backend == null) {
		const fallback = CreateCharacter(Math.random);
		return {draft: ApplyCharacterDraft(options.base, fallback, keys), generatedByModel: false, keys};
	}

	// the entry's OWN name only counts as taken when the name is being REDRAFTED: keeping it is correct
	// otherwise, and including it would trip the retry below on every notes-only draft
	const avoidNames = NormalizeAvoidNames(options.avoidNames, options.base.name, keys.includes("name"));
	let result = await CallLLM(backend, {
		role: "loreGenerator",
		model: options.model ?? null,
		system: LoreCharacter_SystemPrompt(),
		user: LoreCharacter_UserPrompt({
			field: options.field, drafting: keys, override: options.override == true,
			draft: options.base, hint: options.hint, avoidNames,
		}),
		json: LoreCharacter_JSONSchema(),
		maxAttempts: options.maxAttempts ?? 3,
	});

	// one informed re-ask when the model reached for a name that is already taken
	const parsedName = result.parsed != null ? CoerceText((result.parsed as Record<string, unknown>).name) : null;
	if (result.parsed != null && parsedName != null && IsNameTaken(parsedName, avoidNames)) {
		result = await CallLLM(backend, {
			role: "loreGenerator",
			model: options.model ?? null,
			system: LoreCharacter_SystemPrompt(),
			user: LoreCharacter_UserPrompt({
				field: options.field, drafting: keys, override: options.override == true,
				draft: options.base, hint: options.hint, avoidNames,
				fixNotes: `The name "${parsedName}" is already used by another character. Choose a DIFFERENT name.`,
			}),
			json: LoreCharacter_JSONSchema(),
			maxAttempts: 1,
		});
	}

	if (result.parsed == null) {
		// the model path failed; still return something usable, and SAY it was the fallback
		const fallback = CreateCharacter(Math.random);
		return {
			draft: ApplyCharacterDraft(options.base, fallback, keys),
			generatedByModel: false,
			keys,
			error: result.error ?? "The model did not return a usable draft.",
		};
	}

	return {draft: ApplyCharacterDraft(options.base, result.parsed, keys), generatedByModel: true, keys};
}

/** Case-insensitive list of names a draft must not reuse.
 *
 * `ownName` is the edited entry's current name. When it is NOT being redrafted it is removed from the list
 * (an unchanged name is not a collision); when it IS, it is added, so "redraft the name" cannot hand back
 * the same person under the same name. */
function NormalizeAvoidNames(names: string[]|n, ownName: string|n, redraftingName: boolean): string[] {
	const own = (ownName ?? "").trim().toLowerCase();
	const all = [...(names ?? [])]
		.map(a=>a.trim().toLowerCase())
		.filter(a=>a.length > 0 && a != own);
	if (redraftingName && own.length > 0) all.push(own);
	return [...new Set(all)];
}

function IsNameTaken(name: string, avoidNames: string[]): boolean {
	const lowered = name.trim().toLowerCase();
	return lowered.length > 0 && avoidNames.some(a=>a == lowered);
}

export type GenerateWorldDraftOptions = {
	field: LoreWorldField;
	base: EchoWorld;
	/** true = redraft every field instead of only the empty ones (`field: "all"` only) */
	override?: boolean;
	hint?: string|n;
	model?: string|n;
	maxAttempts?: number;
};

export async function GenerateWorldDraft(backend: LLMBackend|n, options: GenerateWorldDraftOptions): Promise<LoreDraftResult<EchoWorld>> {
	const keys = WorldDraftKeysFor(options.field, options.base, options.override == true);
	if (keys.length == 0) {
		return {draft: options.base, generatedByModel: false, keys};
	}

	if (backend == null) {
		const fallback = CreateWorld(Math.random);
		return {draft: ApplyWorldDraft(options.base, fallback, keys), generatedByModel: false, keys};
	}

	const result = await CallLLM(backend, {
		role: "loreGenerator",
		model: options.model ?? null,
		system: LoreWorld_SystemPrompt(),
		user: LoreWorld_UserPrompt({
			field: options.field, drafting: keys, override: options.override == true,
			draft: options.base, hint: options.hint,
		}),
		json: LoreWorld_JSONSchema(),
		maxAttempts: options.maxAttempts ?? 3,
	});

	if (result.parsed == null) {
		const fallback = CreateWorld(Math.random);
		return {
			draft: ApplyWorldDraft(options.base, fallback, keys),
			generatedByModel: false,
			keys,
			error: result.error ?? "The model did not return a usable draft.",
		};
	}

	return {draft: ApplyWorldDraft(options.base, result.parsed, keys), generatedByModel: true, keys};
}
